import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Eye, EyeOff, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-secondary/40">
      <div className="w-full max-w-sm fade-up">
        <div className="mb-8"><BrandLogo size="lg" /></div>
        {!token ? (
          <div className="text-center" data-testid="reset-invalid-token">
            <h2 className="font-display text-2xl font-bold tracking-tight">Invalid reset link</h2>
            <p className="text-muted-foreground text-sm mt-2">This link is missing or malformed. Please request a new password reset.</p>
            <Link to="/forgot-password" className="inline-block mt-6 text-sm text-primary hover:underline font-medium" data-testid="request-new-link">Request a new link</Link>
          </div>
        ) : (
          <>
            <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <KeyRound className="h-6 w-6 text-primary" />
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight">Set a new password</h2>
            <p className="text-muted-foreground text-sm mt-2">Choose a strong password of at least 8 characters.</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">New password</Label>
                <div className="relative">
                  <Input
                    id="password" type={showPw ? "text" : "password"} required data-testid="reset-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-11 pr-11 focus-visible:ring-primary/20 focus-visible:border-primary"
                  />
                  <button type="button" onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? "Hide password" : "Show password"} data-testid="reset-toggle-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-sm font-medium">Confirm password</Label>
                <Input
                  id="confirm" type={showPw ? "text" : "password"} required data-testid="reset-confirm-input"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  className="h-11 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-submit-button"
                className="w-full h-11 bg-primary hover:bg-[#C81219] text-white font-semibold transition-colors">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
            <div className="mt-5 text-center">
              <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium" data-testid="back-to-login-link">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
